import React, { useCallback, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
} from "@material-ui/core";
import {
  append,
  mergeDeepRight,
  mergeRight,
  propEq,
  reject,
  remove,
} from "ramda";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import { isBlank, mapIndexed, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import { makeStyles } from "@material-ui/core/styles";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import AudioTypesDialog from "../edit/AudioTypesDialog";
import EditIcon from "@material-ui/icons/Edit";
import { caseInsensitiveSort } from "../../util/sorts";
import ConfirmButton from "../../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },
}));

const sortAudioTypes = caseInsensitiveSort("display");

const AudioTypesCard = ({ submissionType, project, loading, onUpdate }) => {
  const classes = useStyles();

  const [dialogOpen, setDialogOpen] = useState(false);
  const handleAddClick = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleDialogApply = useCallback(
    async data => {
      //call parent callback to save updated data.
      setDialogOpen(false);
      console.log(data);
      const updatedSubType = mergeRight(submissionType, { audioTypes: data });
      const update = mergeDeepRight(project, {
        submissionTypes: append(
          updatedSubType,
          reject(propEq("name", submissionType.name), project.submissionTypes),
        ),
      });
      console.log(update);
      onUpdate(update);
    },
    [submissionType, project, onUpdate],
  );

  const handleDeleteClick = useCallback(
    async idx => {
      const updatedSubType = mergeRight(submissionType, {
        audioTypes: remove(idx, 1, sortAudioTypes(submissionType.audioTypes)),
      });
      const update = mergeDeepRight(project, {
        submissionTypes: append(
          updatedSubType,
          reject(propEq("name", submissionType.name), project.submissionTypes),
        ),
      });
      console.log(update);
      onUpdate(update);
    },
    [project, submissionType, onUpdate],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "subtitle1",
          color: "primary",
        }}
        title="Audio Types"
        action={
          <Tooltip title="add Audio Types">
            <IconButton onClick={handleAddClick}>
              <EditIcon color="primary" fontSize="large" />
            </IconButton>
          </Tooltip>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {loading && <PanelLoadingIndicator />}
        {isBlank(submissionType) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            Error Loading Audio Types.
          </Box>
        )}
        {isBlank(submissionType.audioTypes) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            No Audio Types set up for this Submission Type.
          </Box>
        )}
        {notBlank(submissionType.audioTypes) && (
          <List dense style={{ maxHeight: 160, overflowY: "auto" }}>
            {mapIndexed(
              (at, idx) => (
                <ListItem key={at.name}>
                  <ListItemText primary={at.display} />
                  <ListItemSecondaryAction>
                    <ConfirmButton
                      handleDelete={() => handleDeleteClick(idx)}
                      dialogTitle="Delete Audio Type"
                      dialogBody="Are you sure you want to delete this audio type?"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ),
              sortAudioTypes(submissionType.audioTypes),
            )}
          </List>
        )}
      </CardContent>

      {dialogOpen && (
        <AudioTypesDialog
          submissionType={submissionType}
          open={dialogOpen}
          onCancel={handleCancel}
          onApply={handleDialogApply}
        />
      )}
    </Card>
  );
};

export default AudioTypesCard;
